import { FC } from 'react';

import CtaButton from '@components/quiz/shared/CtaButton';
import StickyCtaBar from '@components/quiz/shared/StickyCtaBar';
import { useReadiness } from '@components/quiz/ReadinessProvider';
import type { SanityReadinessStakesBlock } from '@lib/sanity/types';

type StakesBlockProps = SanityReadinessStakesBlock & {
  onNext: () => void;
};

/**
 * Step 6: stakes. One question, three league types. Selection moves the
 * WINNERS target marker on the jeopardy bar up — it never moves the
 * user's score down (spec 6.7).
 */
const StakesBlock: FC<StakesBlockProps> = ({ options, ctaText, onNext }) => {
  const { state, actions } = useReadiness();

  return (
    <section className="flex flex-col items-center px-6 pb-28 pt-8">
      <div className="w-full max-w-sm space-y-3">
        {options?.map((option) => {
          const isSelected = state.stakes === option.stakeKey;

          return (
            <button
              key={option._key}
              type="button"
              onClick={() => actions.setStakes(option.stakeKey)}
              data-cy={`readiness-stakes-${option.stakeKey}`}
              className={`flex w-full items-center gap-3 rounded-xl border px-5 py-4 text-left transition-all ${
                isSelected
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-neutral-200 bg-white'
              }`}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                  isSelected
                    ? 'border-primary-600 bg-primary-600'
                    : 'border-neutral-300'
                }`}
              >
                {isSelected && (
                  <span className="h-2 w-2 rounded-full bg-white" />
                )}
              </span>
              <span>
                <span className="block font-heading text-sm font-bold text-neutral-800">
                  {option.label}
                </span>
                {option.sublabel && (
                  <span className="block font-body text-xs text-neutral-500">
                    {option.sublabel}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <StickyCtaBar>
        <CtaButton
          onClick={onNext}
          disabled={!state.stakes}
          dataCy="readiness-stakes-cta"
        >
          {ctaText ?? 'Almost there'}
        </CtaButton>
      </StickyCtaBar>
    </section>
  );
};

export default StakesBlock;
